import { zodResolver } from '@hookform/resolvers/zod';
import CarrierServiceWarning from 'components/shared/CarrierServiceWarning';
import { SettingUrls } from 'constants/SettingsUrls';
import { SettingKey, WizardStepName } from 'data/ppoints/constants';
import useCheckoutMapSettingsSWR from 'features/settings/components/ppoints/design-settings/CheckoutMapSettings/hooks/useCheckoutMapSettingsSWR';
import useActivateIntegrationMode from 'features/integration/hooks/useActivateIntegrationMode';
import IntegrationSelector from 'features/integration/integration-selector/IntegrationSelector';
import WizardStepItemDone from 'features/wizard/components/WizardStepItemDone';
import useReportStepLoading from 'features/wizard/hooks/useReportStepLoading';
import useWizard from 'features/wizard/hooks/useWizard';
import { WizardStepContentProps } from 'features/wizard/models/WizardStepContentProps';
import { useCheckoutMapSettingsMapper } from 'hooks/useCheckoutMapSettingsMapper';
import useContextualSaveBar from 'hooks/useContextualSaveBar';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { IntegrationModeStepForm } from '../../models/integration-mode-step-form';
import integrationModeStepSchema from './schema/integrationModeStepSchema';

const SAVE_BAR_ID = 'wizard-integration-mode-savebar';

export const IntegrationModeStep: React.FC<WizardStepContentProps> = ({ onLoadingChange }) => {
    const navigate = useNavigate();
    const wizard = useWizard();
    const isDone = wizard.isStepDone(WizardStepName.integration_mode_step);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useReportStepLoading(isSubmitting, onLoadingChange);

    const { t, i18n } = useTranslation();

    const tKey = i18n.t.bind(i18n) as (key: string) => string;

    const setting = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);
    const { data: checkoutMapSettingsData, isLoading: isLoadingCheckoutMapSettings } = useCheckoutMapSettingsSWR();
    const { mapToVo } = useCheckoutMapSettingsMapper();

    const form = useForm<IntegrationModeStepForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(integrationModeStepSchema),
        defaultValues: {
            integrationMode: setting.settings ?? undefined,
            googleMapApiKey: mapToVo(checkoutMapSettingsData).google_map_api_key,
        },
    });

    // useForm's defaultValues is only read once at mount, so this covers the setting/checkout map
    // key arriving after that first render (e.g. loaded from redux/SWR asynchronously rather than
    // already cached).
    useEffect(() => {
        if (setting.fetching || !setting.settings || isLoadingCheckoutMapSettings) return;

        form.reset({
            integrationMode: setting.settings,
            googleMapApiKey: mapToVo(checkoutMapSettingsData).google_map_api_key,
        });
    }, [setting.settings, setting.fetching, checkoutMapSettingsData, isLoadingCheckoutMapSettings, mapToVo, form]);

    const integrationModeField = useController({ control: form.control, name: 'integrationMode' });
    const googleMapApiKeyField = useController({ control: form.control, name: 'googleMapApiKey' });

    const { activateIntegrationMode, showCarrierServiceWarning } = useActivateIntegrationMode();

    const onSubmit = useCallback(
        async (data: IntegrationModeStepForm) => {
            const previousDefaults = form.formState.defaultValues as IntegrationModeStepForm;

            setIsSubmitting(true);

            if (!form.formState.isDirty) {
                await wizard.handleWizardStepper(WizardStepName.integration_mode_step);
                setIsSubmitting(false);
                return;
            }

            form.reset(data);

            try {
                const activated = await activateIntegrationMode(data.integrationMode, data.googleMapApiKey);

                if (!activated) {
                    // Keep the selection the merchant made, but restore the dirty state so the save bar stays available.
                    form.reset(previousDefaults);
                    form.reset(data, { keepDefaultValues: true });
                    return;
                }

                shopify.toast.show(t('WIZARD.INTEGRATION_MODE_STEP.TOAST.ACTIVATED'));

                if (!wizard.isAllDone()) {
                    await wizard.handleWizardStepper(WizardStepName.integration_mode_step);
                }
            } finally {
                setIsSubmitting(false);
            }
        },
        [activateIntegrationMode, form, wizard, t]
    );

    const { saveBar } = useContextualSaveBar<IntegrationModeStepForm>(form, onSubmit, SAVE_BAR_ID, false);

    useLayoutEffect(() => {
        return () => {
            shopify.saveBar.hide(SAVE_BAR_ID).catch(() => {});
        };
    }, []);

    if (isDone) {
        return (
            <WizardStepItemDone
                message={t('WIZARD.INTEGRATION_MODE_STEP.DESCRIPTION')}
                action={
                    <s-button onClick={() => navigate(SettingUrls.integrationMode)}>
                        {t('WIZARD.INTEGRATION_MODE_STEP.MODIFY_SETTINGS')}
                    </s-button>
                }
            />
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {saveBar}
            <div>{t('WIZARD.INTEGRATION_MODE_STEP.SELECT_DESCRIPTION')}</div>
            <IntegrationSelector
                value={integrationModeField.field.value}
                onChange={integrationModeField.field.onChange}
                googleMapApiKey={googleMapApiKeyField.field.value}
                onGoogleMapApiKeyChange={googleMapApiKeyField.field.onChange}
                googleMapApiKeyError={
                    googleMapApiKeyField.fieldState.error?.message
                        ? tKey(googleMapApiKeyField.fieldState.error.message)
                        : undefined
                }
            />
            {integrationModeField.fieldState.error?.message && (
                <s-text tone="critical">{tKey(integrationModeField.fieldState.error.message)}</s-text>
            )}
            {showCarrierServiceWarning && <CarrierServiceWarning />}
            <div className="flex justify-end pt-1">
                <s-button
                    variant="primary"
                    loading={isSubmitting}
                    onClick={() => {
                        form.handleSubmit(onSubmit)();
                    }}
                >
                    {t('WIZARD.STEP_NEXT_BUTTON')}
                </s-button>
            </div>
        </div>
    );
};
